// Pure timer display maths (architecture sprint-04 §5.3). The client clock is never compared with
// segmentStartedAt: elapsed = server workedSeconds + the local monotonic delta since receipt, so
// clock skew cannot distort the display.
import type { TimerSnapshot } from '@/types/timer';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

export function elapsedSeconds(s: TimerSnapshot, perfNow: number): number {
  if (s.timer.pausedAt !== null) return s.timer.workedSeconds;
  return s.timer.workedSeconds + Math.max(0, Math.floor((perfNow - s.receivedAt) / MS_PER_SECOND));
}

/**
 * The server's "now" projected forward with the local monotonic clock. The delta is clamped at 0: a
 * tick value captured before the snapshot arrived must not move the server's now backwards (that
 * would delay a just-due check-in by up to one tick).
 */
export function serverNowMs(s: TimerSnapshot, perfNow: number): number {
  return Date.parse(s.timer.serverNow) + Math.max(0, perfNow - s.receivedAt);
}

/** 'HH:MM:SS', hours at least two digits: 0 → '00:00:00', 360005 → '100:00:05'. */
export function formatElapsed(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / SECONDS_PER_HOUR);
  const minutes = Math.floor((safe % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = safe % SECONDS_PER_MINUTE;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
